import * as React from 'react'
import { observer } from 'mobx-react'
import CreateAmendmentViewModel, {
  IAmendmentVisibilityOption,
} from './CreateAmendmentViewModel'
import {
  Form,
  FormColumn,
  FormColumns,
  FormRow,
  FormGroupContainer,
  TextInputContainer,
  NumericInputContainer,
} from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import {
  CustomSelect,
  ItemSource,
  RenderItemProps,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import Icon from 'components/Icon'
import Button from 'components/Button'
import ErrorBlock from 'components/ErrorBlock'
import styles from './CreateAmendment.sass'

interface Props {
  vm: CreateAmendmentViewModel
}

export default observer(function CreateAmendmentDialog({ vm }: Props) {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.visibilityOption, (item, text) => {
        return item.visibility.toLowerCase().includes(text.toLowerCase())
      }),
    [vm.visibility],
  )
  const selectProps = useCustomSelectStateFromItemSource({ itemSource })

  const descriptionError = vm.validation.getError('description')
  const internalNoteError = vm.validation.getError('internalNote')
  const visibilityError = vm.validation.getError('visibility')

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.createAmendment.pending ? undefined : vm.close}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>New Amendment</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            If the Client wants additional service, the Pro for reasons wants
            more earnings, or the Client wants a refund or reduced services, you
            can add amendments to the job here.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <Form onSubmit={vm.createAmendment}>
            <FormRow>
              <TextInputContainer
                model={vm}
                label="Description"
                field="description"
                name="description"
                placeholder="E.g. the client wanted additional services, or requested a refund"
                error={descriptionError}
                status={descriptionError ? 'danger' : 'default'}
                maxLength={vm.MAX_DESCRIPTION_LENGTH}
                data-test="amendment-description-input"
              />
            </FormRow>
            <FormRow>
              <TextInputContainer
                model={vm}
                label="Internal note"
                field="internalNote"
                name="internal note"
                help="For our own internal reference"
                error={internalNoteError}
                status={internalNoteError ? 'danger' : 'default'}
                maxLength={vm.MAX_INTERNAL_NOTE_LENGTH}
                data-test="amendment-internal-note-input"
              />
            </FormRow>
            <FormRow>
              <FormGroupContainer
                model={vm}
                label="Visibility"
                field={'visibilityOption'}
                error={visibilityError}
              >
                <CustomSelect
                  {...selectProps}
                  multi={false}
                  value={vm.option}
                  placeholder="Select"
                  onChange={vm.setVisibilityOption}
                  itemToString={(i) => i.displayName}
                  renderItem={(itemProps) => (
                    <SelectedVisibility itemProps={itemProps} />
                  )}
                  icon={
                    <Icon size={24} name={'dropdown'} className={styles.icon} />
                  }
                  status={visibilityError ? 'danger' : 'default'}
                  data-test="amendment-visibility-dropdown"
                />
              </FormGroupContainer>
            </FormRow>
            <FormRow>
              <FormColumns>
                <FormColumn>
                  <NumericInputContainer
                    model={vm}
                    precision={2}
                    label="Client amount"
                    field="clientAmount"
                    data-test="add-amendment-modal-client-amount-input"
                    help="If positive, client pays more. If negative, client is refunded"
                  />
                </FormColumn>
                <FormColumn>
                  <NumericInputContainer
                    model={vm}
                    precision={2}
                    label="Pro amount"
                    field="providerAmount"
                    data-test="add-amendment-modal-pro-amount-input"
                    help="If positive, pro gets more. If negative, pro is penalised"
                  />
                </FormColumn>
              </FormColumns>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {vm.createAmendment.rejected && (
        <ErrorBlock
          tackOn="bottom"
          error={vm.createAmendment.error}
          onCloseClick={() => vm.createAmendment.reset()}
        />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            dataTest="add-amendment-close-button"
          >
            Close
          </Button>
          <Button
            type="primary"
            onClick={vm.createAmendment}
            loading={vm.createAmendment.pending}
            dataTest="add-amendment-submit-button"
          >
            Save
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface SelectedVisibilityProps {
  itemProps: RenderItemProps<IAmendmentVisibilityOption>
}

function SelectedVisibility({ itemProps }: SelectedVisibilityProps) {
  return <SingleItemSelectedDefault {...itemProps} />
}
